import * as React from "react";
import { httpsCallable } from "firebase/functions";
import {
    ClientRepairDiagnosticsPlanAction,
    ClientRepairDiagnosticsRequest,
    ClientRepairDiagnosticsResponse,
} from "intake_shared/src/types";
import { fxs } from "../../../../../utils/firestore";
import Alert from "../../../bits/Alert/Alert";
import { AlertDialog } from "../../../bits/AlertDialog/AlertDialog";
import { Button } from "../../../bits/Button/Button";
import { Card } from "../../../bits/Card/Card";
import { Input } from "../../../bits/Input/Input";
import styles from "./ClientRepairDiagnostics.module.scss";

type StatusMessage = {
    type: "info" | "success" | "warning" | "danger";
    text: string;
} | null;

const clientRepairDiagnostics = httpsCallable<
    ClientRepairDiagnosticsRequest,
    ClientRepairDiagnosticsResponse
>(fxs, "httpFunctionClientRepairDiagnostics");

function getDefaultSelectedActionIds(actions: ClientRepairDiagnosticsPlanAction[]) {
    return actions.filter((action) => action.safeToApply).map((action) => action.id);
}

function getPlanSearchPayload(plan: ClientRepairDiagnosticsResponse) {
    return {
        clientEmail: plan.evidence.searchedClientEmail || "",
        clientId: plan.evidence.searchedClientId || "",
        clinicianEmail: plan.evidence.searchedClinicianEmail || "",
    };
}

export default function ClientRepairDiagnostics() {
    const [clientEmail, setClientEmail] = React.useState("");
    const [clientId, setClientId] = React.useState("");
    const [clinicianEmail, setClinicianEmail] = React.useState("");
    const [plan, setPlan] = React.useState<ClientRepairDiagnosticsResponse | null>(
        null
    );
    const [selectedActionIds, setSelectedActionIds] = React.useState<string[]>([]);
    const [isPlanning, setIsPlanning] = React.useState(false);
    const [isApplying, setIsApplying] = React.useState(false);
    const [showApplyDialog, setShowApplyDialog] = React.useState(false);
    const [status, setStatus] = React.useState<StatusMessage>(null);

    const canPlan = Boolean(clientEmail.trim() || clientId.trim());

    const runPlan = async (event?: React.FormEvent) => {
        event?.preventDefault();
        if (!canPlan) return;

        setIsPlanning(true);
        setPlan(null);
        setSelectedActionIds([]);
        setStatus({ type: "info", text: "Checking for issues..." });
        try {
            const response = await clientRepairDiagnostics({
                action: "plan",
                payload: {
                    clientEmail: clientEmail.trim(),
                    clientId: clientId.trim(),
                    clinicianEmail: clinicianEmail.trim(),
                },
            });
            setPlan(response.data);
            setSelectedActionIds(getDefaultSelectedActionIds(response.data.actions));
            setStatus({
                type: "success",
                text: "Review the suggested fixes below before applying.",
            });
        } catch (error) {
            setStatus({
                type: "warning",
                text: "No results found. Check the details entered and try again.",
            });
        } finally {
            setIsPlanning(false);
        }
    };

    const confirmApply = () => {
        if (!plan || selectedActionIds.length === 0) return;
        setShowApplyDialog(true);
    };

    const applySelectedActions = async () => {
        if (!plan || selectedActionIds.length === 0) return;
        setShowApplyDialog(false);

        setIsApplying(true);
        setStatus({ type: "info", text: "Applying fixes..." });
        try {
            const searchPayload = getPlanSearchPayload(plan);
            const response = await clientRepairDiagnostics({
                action: "apply",
                payload: {
                    ...searchPayload,
                    actionIds: selectedActionIds,
                },
            });
            setPlan(response.data);
            setSelectedActionIds(getDefaultSelectedActionIds(response.data.actions));
            const appliedCount = response.data.appliedActionIds.length;
            const skippedCount = response.data.skippedActionIds.length;
            setStatus({
                type: appliedCount > 0 ? "success" : "warning",
                text:
                    appliedCount > 0
                        ? `${appliedCount} fix(es) applied.${
                              skippedCount > 0
                                  ? ` ${skippedCount} could not be applied.`
                                  : ""
                          }`
                        : "No fixes could be applied. Review the plan for details.",
            });
        } catch (error) {
            setStatus({
                type: "warning",
                text: "Something went wrong. Please try again.",
            });
        } finally {
            setIsApplying(false);
        }
    };

    const toggleAction = (actionId: string) => {
        setSelectedActionIds((current) =>
            current.includes(actionId)
                ? current.filter((id) => id !== actionId)
                : [...current, actionId]
        );
    };

    return (
        <div className={styles.container}>
            <AlertDialog
                open={showApplyDialog}
                onClose={() => setShowApplyDialog(false)}
                title="Confirm Fixes"
                description={`Apply ${selectedActionIds.length} selected fix(es) to this client's records?`}
                saveText="Apply"
                cancelText="Cancel"
                onSaveClick={applySelectedActions}
                onCancelClick={() => setShowApplyDialog(false)}
            />

            <Card classNames={styles.searchCard}>
                <div className={styles.header}>
                    <h2>Client Repair</h2>
                    <p>
                        Search by client email or client ID to find and fix booking
                        issues. You can also filter by clinician email.
                    </p>
                </div>
                <form onSubmit={runPlan} className={styles.searchForm}>
                    <Input
                        label="Client email"
                        type="email"
                        placeholder="client@example.com"
                        value={clientEmail}
                        onChange={(event) => setClientEmail(event.target.value)}
                    />
                    <Input
                        label="Client ID"
                        placeholder="e.g. ABC123"
                        value={clientId}
                        onChange={(event) => setClientId(event.target.value)}
                    />
                    <Input
                        label="Clinician email (optional)"
                        type="email"
                        placeholder="clinician@example.com"
                        value={clinicianEmail}
                        onChange={(event) => setClinicianEmail(event.target.value)}
                        isOptional
                    />
                    <Button
                        type="submit"
                        disabled={!canPlan || isPlanning || isApplying}
                        className={styles.planButton}
                    >
                        {isPlanning ? "Planning..." : "Run Plan"}
                    </Button>
                </form>
                {status && <Alert type={status.type}>{status.text}</Alert>}
            </Card>

            {plan && (
                <>
                    <div className={styles.summaryGrid}>
                        <EvidenceCard
                            title="Active clients"
                            count={plan.evidence.activeClients.length}
                            rows={plan.evidence.activeClients.map((client) => ({
                                id: client.id,
                                text: `${client.displayName || "Unnamed client"} - ${
                                    client.status || "No status"
                                }`,
                            }))}
                        />
                        <EvidenceCard
                            title="History clients"
                            count={plan.evidence.historyClients.length}
                            rows={plan.evidence.historyClients.map((client) => ({
                                id: client.id,
                                text: `${client.displayName || "Unnamed client"} - ${
                                    client.status || "No status"
                                }`,
                            }))}
                        />
                        <EvidenceCard
                            title="Data backup"
                            count={plan.evidence.exportedClients.length}
                            rows={plan.evidence.exportedClients.map((client) => {
                                const counts = client.exportedAppointmentCounts;
                                return {
                                    id: client.id,
                                    text: `${
                                        client.displayName || "Unnamed client"
                                    } - ${counts?.total || 0} appointments, ${
                                        counts?.cancelled || 0
                                    } cancelled`,
                                };
                            })}
                        />
                    </div>

                    <Card classNames={styles.actionsCard}>
                        <div className={styles.actionsHeader}>
                            <div>
                                <h3>Suggested Fixes</h3>
                                <p>
                                    Safe fixes are pre-selected. Blocked fixes need
                                    attention before they can be applied.
                                </p>
                            </div>
                            <Button
                                onClick={confirmApply}
                                disabled={
                                    selectedActionIds.length === 0 ||
                                    isPlanning ||
                                    isApplying
                                }
                            >
                                {isApplying
                                    ? "Applying..."
                                    : `Apply Selected (${selectedActionIds.length})`}
                            </Button>
                        </div>

                        {plan.appliedActionIds.length > 0 && (
                            <Alert type="success">
                                {plan.appliedActionIds.length} fix(es) applied
                                successfully.
                            </Alert>
                        )}
                        {plan.skippedActionIds.length > 0 && (
                            <Alert type="warning">
                                {plan.skippedActionIds.length} fix(es) could not be
                                applied.
                                {plan.blockers.length > 0 && (
                                    <ul>
                                        {plan.blockers.map((blocker, index) => (
                                            <li key={index}>{blocker}</li>
                                        ))}
                                    </ul>
                                )}
                            </Alert>
                        )}
                        {plan.actions.length === 0 && (
                            <Alert type="info">
                                No issues found for this client.
                            </Alert>
                        )}

                        <div className={styles.actionList}>
                            {plan.actions.map((action) => (
                                <div key={action.id} className={styles.actionRow}>
                                    <label className={styles.actionSelect}>
                                        <input
                                            type="checkbox"
                                            checked={selectedActionIds.includes(
                                                action.id
                                            )}
                                            disabled={!action.safeToApply}
                                            onChange={() => toggleAction(action.id)}
                                            aria-label={`Select ${action.title}`}
                                        />
                                    </label>
                                    <div className={styles.actionBody}>
                                        <div className={styles.actionTitleRow}>
                                            <h4>{action.title}</h4>
                                            <span
                                                className={
                                                    action.safeToApply
                                                        ? styles.safeBadge
                                                        : styles.blockedBadge
                                                }
                                            >
                                                {action.safeToApply
                                                    ? "Safe"
                                                    : "Blocked"}
                                            </span>
                                        </div>
                                        <p>{action.description}</p>
                                        <div className={styles.actionMeta}>
                                            <span>Client: {action.clientId}</span>
                                            {action.clinicianId && (
                                                <span>
                                                    Clinician: {action.clinicianId}
                                                </span>
                                            )}
                                            {action.bookingId && (
                                                <span>
                                                    Booking: {action.bookingId}
                                                </span>
                                            )}
                                        </div>
                                        {action.evidence.length > 0 && (
                                            <ul>
                                                {action.evidence.map(
                                                    (item, index) => (
                                                        <li
                                                            key={`${action.id}:evidence:${index}`}
                                                        >
                                                            {item}
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        )}
                                        {action.blockers.length > 0 && (
                                            <div className={styles.blockers}>
                                                {action.blockers.map(
                                                    (blocker, index) => (
                                                        <div
                                                            key={`${action.id}:blocker:${index}`}
                                                        >
                                                            {blocker}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
}

function EvidenceCard({
    title,
    count,
    rows,
}: {
    title: string;
    count: number;
    rows: Array<{ id: string; text: string }>;
}) {
    return (
        <Card classNames={styles.evidenceCard}>
            <div className={styles.evidenceHeader}>
                <span>{title}</span>
                <strong>{count}</strong>
            </div>
            {rows.length > 0 ? (
                <div className={styles.evidenceRows}>
                    {rows.map((row) => (
                        <div key={row.id}>
                            <span>{row.text}</span>
                            <small>{row.id}</small>
                        </div>
                    ))}
                </div>
            ) : (
                <p className={styles.emptyState}>No records found</p>
            )}
        </Card>
    );
}
